import { useState } from 'react';
import type { ReactNode } from 'react';

import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import FingerprintRoundedIcon from '@mui/icons-material/FingerprintRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { Box, Button, Chip, CircularProgress, Skeleton, Stack, Typography } from '@mui/material';

import { accountConsoleUrl } from '@/shared/auth/accountConsole';
import { startPasskeyEnrolment } from '@/shared/auth/passkeyEnrolment';
import { useWebAuthnCapability } from '@/shared/auth/useWebAuthnCapability';
import type { WebAuthnCapability } from '@/shared/auth/useWebAuthnCapability';
import { useSession } from '@/shared/session';
import { FieldRow, SettingsCard } from '@/shared/ui';

/** Colour + label + optional icon — status is never colour alone (design doc §15). */
function StatusChip({
  passkeyEnrolled,
  loading,
  t,
}: {
  passkeyEnrolled: boolean | null;
  loading: boolean;
  t: TFunction;
}) {
  if (loading) {
    return <Skeleton variant="rounded" width={96} height={24} />;
  }
  if (passkeyEnrolled === true) {
    return (
      <Chip
        size="small"
        color="success"
        icon={<CheckRoundedIcon fontSize="small" />}
        label={t('security.passkey.status.setUp')}
      />
    );
  }
  if (passkeyEnrolled === false) {
    return <Chip size="small" variant="outlined" label={t('security.passkey.status.notSetUp')} />;
  }
  // Absent hint: honestly "unknown" — never rendered as "not set up" (adr-merchant-portal-020 D3).
  return (
    <Chip
      size="small"
      variant="outlined"
      icon={<HelpOutlineRoundedIcon fontSize="small" />}
      label={t('security.passkey.status.unknown')}
    />
  );
}

/** What this browser/device can do — a second, independent axis from the enrolment status above. */
function DeviceCapabilityLine({ capability, t }: { capability: WebAuthnCapability; t: TFunction }) {
  if (capability === 'checking') {
    return (
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Skeleton variant="circular" width={20} height={20} />
        <Skeleton variant="text" width={240} />
      </Stack>
    );
  }

  const config: Record<
    Exclude<WebAuthnCapability, 'checking'>,
    { icon: ReactNode; text: string }
  > = {
    platform: {
      icon: <CheckCircleOutlineRoundedIcon fontSize="small" color="success" aria-hidden />,
      text: t('security.passkey.device.platform'),
    },
    'cross-device': {
      icon: <InfoOutlinedIcon fontSize="small" color="info" aria-hidden />,
      text: t('security.passkey.device.crossDevice'),
    },
    unsupported: {
      icon: <WarningAmberRoundedIcon fontSize="small" color="warning" aria-hidden />,
      text: t('security.passkey.device.unsupported'),
    },
  };
  const { icon, text } = config[capability];

  return (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      {icon}
      <Typography variant="body2" color="text.secondary">
        {text}
      </Typography>
    </Stack>
  );
}

/**
 * «Вход по Face ID / Touch ID» — the passkey self-service card (user-story-merchant-portal-060,
 * design doc §7–§9). Fetches nothing of its own: the enrolment hint is a session projection
 * (`useSession().passkeyEnrolled`), the device capability comes from the browser
 * (`useWebAuthnCapability`), and the round trip is a full-page navigation, never an API call.
 *
 * Twelve states fall out of two independent axes plus a transient one:
 *  - enrolment hint: `true` / `false` / `null` ("unknown" — the BFF hint is absent; NEVER shown as
 *    "not set up");
 *  - device capability: `platform` / `cross-device` / `unsupported` (no primary CTA at all — a
 *    disabled button with no way to enable it would be a dead end) / `checking` (skeletons);
 *  - `redirecting`, once the primary CTA is clicked (spinner, disabled, `aria-busy`, no reset needed
 *    since the document is about to unload).
 */
export function PasskeyCard() {
  const { t } = useTranslation('account');
  const session = useSession();
  const capability = useWebAuthnCapability();
  const [redirecting, setRedirecting] = useState(false);

  const { passkeyEnrolled } = session;
  const sessionLoading = session.status === 'loading';

  const handleSetUp = () => {
    setRedirecting(true);
    startPasskeyEnrolment({
      source: 'card',
      returnPath: '/account/security',
      enrolledBefore: passkeyEnrolled,
    });
  };

  const statusHintText = t(
    passkeyEnrolled === true
      ? 'security.passkey.status.setUpHint'
      : passkeyEnrolled === false
        ? 'security.passkey.status.notSetUpHint'
        : 'security.passkey.status.unknownHint',
  );

  const showPrimaryCta = capability !== 'unsupported';
  const primaryCtaLabel =
    passkeyEnrolled === true
      ? t('security.passkey.actions.addDevice')
      : capability === 'cross-device'
        ? t('security.passkey.actions.setUpCrossDevice')
        : t('security.passkey.actions.setUp');

  const manageHref = accountConsoleUrl('#/security/signing-in', '/account/security');
  const manageLabel = t('security.passkey.actions.manage');

  return (
    <SettingsCard
      icon={<FingerprintRoundedIcon />}
      title={t('security.passkey.title')}
      description={t('security.passkey.description')}
      action={<StatusChip passkeyEnrolled={passkeyEnrolled} loading={sessionLoading} t={t} />}
    >
      <FieldRow label={t('security.passkey.status.label')}>
        {sessionLoading ? (
          <Skeleton variant="text" width={280} />
        ) : (
          <Typography variant="body2" color="text.secondary">
            {statusHintText}
          </Typography>
        )}
      </FieldRow>
      <FieldRow
        label={t('security.passkey.device.label')}
        description={t('security.passkey.device.description')}
        alignTop
      >
        <DeviceCapabilityLine capability={capability} t={t} />
      </FieldRow>
      <Box sx={{ pt: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
          {showPrimaryCta ? (
            <Button
              variant="contained"
              startIcon={
                redirecting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <FingerprintRoundedIcon />
                )
              }
              disabled={redirecting || capability === 'checking'}
              aria-busy={redirecting}
              onClick={handleSetUp}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              {redirecting ? t('security.passkey.actions.redirecting') : primaryCtaLabel}
            </Button>
          ) : null}
          <Button
            variant="text"
            component="a"
            href={manageHref}
            target="_blank"
            rel="noopener noreferrer"
            endIcon={<OpenInNewRoundedIcon fontSize="small" aria-hidden />}
            aria-label={`${manageLabel} — ${t('security.other.opensInNewTab')}`}
          >
            {manageLabel}
          </Button>
        </Stack>
        {/*
          «Как это работает» (security.passkey.help) is deliberately not linked yet — the help
          article does not exist (design doc §18 Q3). The key ships translated so the link is a
          one-line addition once the wiki page is written.
        */}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          {t('security.passkey.footnote')}
        </Typography>
      </Box>
    </SettingsCard>
  );
}
